import { Card, CardContent, CardHeader, CardTitle } from '@predoc/cow';
import { DocumentTemplateShell, TemplateEmptyState } from './TemplateShared';
import { SignedUrlPreview } from './SignedUrlPreview';
import {
  DASH,
  formatShortDate,
  handleTemplateAnchorClick,
} from './templateUtils';
import type { TemplatePatient, TemplatePractitioner } from './templateUtils';
import { DocumentReferenceStructuredContent } from './document-reference/DocumentReferenceStructuredContent';
import { getDocumentReferencePreviewContent } from './document-reference/document-reference-content';
import { isExpandableSignedUrlPreview } from './signedUrlPreviewUtils';

export type EncountersTemplatePractitioner = TemplatePractitioner;

export interface EncountersTemplateLocation {
  name?: string;
  locationType?: string;
  addressUse?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface EncountersTemplateCondition {
  conditionName?: string;
  conditionOnsetDate?: string;
  conditionRecordedDate?: string;
  conditionClinicalStatus?: string;
  conditionCategoryCodeName?: string;
  conditionText?: string;
}

export interface EncountersTemplateDocumentReference {
  referenceType?: string;
  contentType?: string;
  signedUrl?: string;
}

export interface EncountersTemplateEncounter {
  id: string;
  name?: string;
  encounterText?: string;
  typeName?: string;
  startTime?: string;
  endTime?: string;
  hospitalizationDischargeDisposition?: string;
  locations: EncountersTemplateLocation[];
  conditions: EncountersTemplateCondition[];
  practitioners: EncountersTemplatePractitioner[];
  documentReferences: EncountersTemplateDocumentReference[];
}

export interface EncountersTemplateProps {
  encounters: EncountersTemplateEncounter[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightEncounterId?: string;
}

const getEncounterDisplayDate = (encounter: EncountersTemplateEncounter) =>
  encounter.startTime ?? encounter.endTime;

const getEncounterTitle = (encounter: EncountersTemplateEncounter) =>
  encounter.name?.trim() || encounter.typeName?.trim() || 'Encounter';

const getEncounterSpecialty = (encounter: EncountersTemplateEncounter) => {
  const specialties = encounter.practitioners
    .map((practitioner) => practitioner.roleSpecialty?.trim())
    .filter(Boolean);
  return specialties[0] || DASH;
};

const getEncounterNotes = (encounter: EncountersTemplateEncounter) =>
  encounter.hospitalizationDischargeDisposition?.trim() || DASH;

const sortEncountersByRecency = (encounters: EncountersTemplateEncounter[]) =>
  [...encounters].sort((a, b) => {
    const dateA = new Date(getEncounterDisplayDate(a) ?? 0).getTime();
    const dateB = new Date(getEncounterDisplayDate(b) ?? 0).getTime();
    const safeA = Number.isNaN(dateA) ? 0 : dateA;
    const safeB = Number.isNaN(dateB) ? 0 : dateB;
    return safeB - safeA;
  });

const allRecordedEncountersHeaders = [
  'Encounter',
  'Dates',
  'Specialty',
  'Notes',
  'Page',
] as const;

const encounterTableGridClass =
  'grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.5fr)]';

const getEncounterTableCellClass = (
  columnIndex: number,
  totalColumns: number,
  variant: 'header' | 'body' = 'body',
) => {
  const borderColor =
    variant === 'header' ? 'border-zinc-300' : 'border-zinc-200';
  const borderClass =
    columnIndex < totalColumns - 1 ? `border-r ${borderColor}` : '';
  const alignClass =
    columnIndex === totalColumns - 1 ? 'text-right' : 'text-left';

  return `px-3 py-2.5 ${borderClass} ${alignClass}`;
};

const AllRecordedEncountersTable = ({
  encounters,
  highlightEncounterId,
}: {
  encounters: EncountersTemplateEncounter[];
  highlightEncounterId?: string;
}) => (
  <div className="flex flex-col gap-3 rounded-sm border border-zinc-200 bg-white p-5">
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-[14px] leading-tight font-bold text-black">
          All Recorded Encounters
        </p>
        <p className="text-[10px] leading-tight text-zinc-500 italic">
          Sorted by recency
        </p>
      </div>
      <p className="text-[9px] leading-tight text-zinc-500 italic">
        *Refer to appendix below for more details
      </p>
    </div>

    <div className="overflow-hidden rounded-sm border border-zinc-200">
      <div className={`${encounterTableGridClass} bg-slate-100`}>
        {allRecordedEncountersHeaders.map((header, idx) => (
          <div
            key={header}
            className={getEncounterTableCellClass(
              idx,
              allRecordedEncountersHeaders.length,
              'header',
            )}
          >
            <p className="text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
              {header}
            </p>
          </div>
        ))}
      </div>

      {encounters.map((encounter, idx) => {
        const isHighlighted =
          !!highlightEncounterId && encounter.id === highlightEncounterId;
        const zebra = idx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
        const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
        const anchorId = `encounter-${encounter.id}`;
        const pageNumber = idx + 2;
        const cellClass = (columnIndex: number) =>
          getEncounterTableCellClass(
            columnIndex,
            allRecordedEncountersHeaders.length,
          );

        return (
          <div
            key={encounter.id}
            className={`${encounterTableGridClass} border-t border-zinc-200 ${rowBg}`}
          >
            <div className={cellClass(0)}>
              <p className="text-[10px] leading-tight text-zinc-950">
                {getEncounterTitle(encounter)}
              </p>
            </div>
            <div className={cellClass(1)}>
              <p className="text-[10px] leading-tight text-zinc-950">
                {formatShortDate(getEncounterDisplayDate(encounter))}
              </p>
            </div>
            <div className={cellClass(2)}>
              <p className="text-[10px] leading-tight text-zinc-950">
                {getEncounterSpecialty(encounter)}
              </p>
            </div>
            <div className={cellClass(3)}>
              <p className="text-[10px] leading-tight text-zinc-950">
                {getEncounterNotes(encounter)}
              </p>
            </div>
            <div className={cellClass(4)}>
              <a
                href={`#${anchorId}`}
                className="text-[10px] leading-tight text-blue-600 hover:underline"
                onClick={(event) => handleTemplateAnchorClick(event, anchorId)}
              >
                {pageNumber}
              </a>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

interface PanelSectionProps {
  title: string;
  children: React.ReactNode;
}

const PanelSection = ({ title, children }: PanelSectionProps) => (
  <div className="flex flex-col gap-2 overflow-hidden rounded-sm border border-gray-400">
    <div className="bg-slate-200 px-3 pt-2.5 pb-1.5">
      <p className="text-[10px] leading-tight font-medium text-gray-900">
        {title}
      </p>
    </div>
    <div className="px-3 pb-2">{children}</div>
  </div>
);

const EncounterNotes = ({ value }: { value?: string }) => {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    return (
      <p className="text-[10px] leading-tight whitespace-pre-line text-black">
        {DASH}
      </p>
    );
  }

  const preview = getDocumentReferencePreviewContent(trimmedValue);
  if (preview.kind === 'structured') {
    return <DocumentReferenceStructuredContent content={preview.content} />;
  }

  return (
    <p className="text-[10px] leading-tight whitespace-pre-line text-black">
      {preview.content || DASH}
    </p>
  );
};

interface LocationColumnProps {
  location?: EncountersTemplateLocation;
}

const LocationColumn = ({ location }: LocationColumnProps) => (
  <div className="flex flex-1 flex-col gap-1 text-[10px] leading-tight text-black">
    <p className="font-bold">{location?.name?.trim() || DASH}</p>
    <p>{location?.locationType?.trim() || DASH}</p>
    <p>{location?.address?.trim() || DASH}</p>
    <p>
      {[location?.city, location?.state].filter(Boolean).join(', ') || DASH}
    </p>
    <p>{location?.postalCode?.trim() || DASH}</p>
  </div>
);

const SummarySection = ({
  encounter,
}: {
  encounter: EncountersTemplateEncounter;
}) => (
  <PanelSection title="Summary">
    <EncounterNotes value={encounter.encounterText} />
  </PanelSection>
);

const LocationsSection = ({
  locations,
}: {
  locations: EncountersTemplateLocation[];
}) => {
  const rows: EncountersTemplateLocation[][] = [];
  for (let i = 0; i < locations.length; i += 2) {
    rows.push(locations.slice(i, i + 2));
  }

  return (
    <PanelSection title="Locations">
      <div className="flex flex-col gap-2">
        {rows.map((row, rowIdx) => (
          <div key={`location-row-${rowIdx}`} className="flex gap-2">
            <LocationColumn location={row[0]} />
            <LocationColumn location={row[1]} />
          </div>
        ))}
      </div>
    </PanelSection>
  );
};

const DocumentsSection = ({
  documents,
}: {
  documents: EncountersTemplateDocumentReference[];
}) => {
  if (!documents.length) return null;

  return (
    <PanelSection title="Documents associated with encounter">
      <div className="flex flex-col gap-3">
        {documents.map((doc, idx) => {
          const expandsPreview = isExpandableSignedUrlPreview(doc.contentType);
          const documentLabel =
            doc.referenceType?.trim() || `Document ${idx + 1}`;

          return (
            <div key={`doc-${idx}`} className="flex flex-col gap-1.5">
              <p className="text-[10px] leading-tight font-medium text-gray-900">
                {documentLabel}
              </p>
              {doc.signedUrl ? (
                <div
                  className={`overflow-hidden rounded-sm border border-zinc-200 bg-white ${
                    expandsPreview
                      ? ''
                      : 'flex h-48 items-center justify-center bg-zinc-100'
                  }`}
                >
                  <SignedUrlPreview
                    signedUrl={doc.signedUrl}
                    contentType={doc.contentType}
                    title={`Encounter document ${idx + 1} preview`}
                  />
                </div>
              ) : (
                <p className="text-[10px] leading-tight text-zinc-500">
                  {DASH}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </PanelSection>
  );
};

const EncounterCard = ({
  encounter,
  highlightEncounterId,
}: {
  encounter: EncountersTemplateEncounter;
  index: number;
  highlightEncounterId?: string;
}) => {
  const isHighlighted =
    !!highlightEncounterId && encounter.id === highlightEncounterId;
  const panelBorder = isHighlighted ? 'border-amber-300' : 'border-gray-200';

  return (
    <Card
      id={`encounter-${encounter.id}`}
      tabIndex={isHighlighted ? -1 : undefined}
      className={`flex flex-col overflow-hidden rounded-sm border-2 bg-white p-0 pb-4 shadow-none ${panelBorder}`}
    >
      <CardHeader className="flex flex-col gap-2 rounded-none bg-slate-700 px-5 pt-5 pb-3 text-white">
        <CardTitle className="text-[14px] leading-tight font-semibold text-white">
          {getEncounterTitle(encounter)}
        </CardTitle>
        <div className="text-[10px] leading-tight">
          <span className="font-semibold">Dates: </span>
          {formatShortDate(getEncounterDisplayDate(encounter))}
        </div>
      </CardHeader>

      <CardContent className="mx-5 mt-3 flex flex-col gap-3 p-0">
        {encounter.encounterText?.trim() ? (
          <SummarySection encounter={encounter} />
        ) : null}
        {encounter.locations.length > 0 ? (
          <LocationsSection locations={encounter.locations} />
        ) : null}
        <DocumentsSection documents={encounter.documentReferences} />
      </CardContent>
    </Card>
  );
};

export const EncountersTemplate = ({
  encounters,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightEncounterId,
}: EncountersTemplateProps) => {
  const items = encounters ?? [];
  const sortedItems = sortEncountersByRecency(items);
  const hasItems = sortedItems.length > 0;

  return (
    <DocumentTemplateShell
      title="Encounters Report"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
      headerBg="slate"
    >
      <section className="flex flex-1 flex-col gap-5 px-8 py-5">
        {!hasItems ? (
          <TemplateEmptyState message="No encounters available" />
        ) : (
          <>
            <AllRecordedEncountersTable
              encounters={sortedItems}
              highlightEncounterId={highlightEncounterId}
            />
            {sortedItems.map((encounter, index) => (
              <EncounterCard
                key={encounter.id ?? `enc-${index}`}
                encounter={encounter}
                index={index}
                highlightEncounterId={highlightEncounterId}
              />
            ))}
          </>
        )}
      </section>
    </DocumentTemplateShell>
  );
};
